import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface SettingsState {
  backgroundMusicEnabled: boolean
  clickSoundEnabled: boolean
  musicVolume: number
  // Transient flag: when true, ambient BGM is paused so another media element
  // (e.g. the mission intro video) can play without sound mixing. Not persisted.
  musicDucked: boolean
  /**
   * Graphics quality. `true` = Ultra mode: ambient clouds on the land page, drifting edge fog on
   * the world map, and the waving alliance flag. `false` = Normal mode: all three are disabled for
   * a lighter, calmer presentation. Default Ultra to preserve the existing animated experience.
   */
  ultraModeEnabled: boolean
}

const initialState: SettingsState = {
  backgroundMusicEnabled: true,
  clickSoundEnabled: true,
  /** 0–1 scale; default = 25% so background music sits well below SFX without users having to tweak. */
  musicVolume: 0.25,
  musicDucked: false,
  ultraModeEnabled: true,
}

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setBackgroundMusicEnabled: (state, action: PayloadAction<boolean>) => {
      state.backgroundMusicEnabled = action.payload
      // Save to localStorage
      localStorage.setItem('backgroundMusicEnabled', String(action.payload))
    },
    setClickSoundEnabled: (state, action: PayloadAction<boolean>) => {
      state.clickSoundEnabled = action.payload
      // Save to localStorage
      localStorage.setItem('clickSoundEnabled', String(action.payload))
    },
    setMusicVolume: (state, action: PayloadAction<number>) => {
      state.musicVolume = action.payload
      // Save to localStorage
      localStorage.setItem('musicVolume', String(action.payload))
    },
    setMusicDucked: (state, action: PayloadAction<boolean>) => {
      state.musicDucked = action.payload
    },
    setUltraModeEnabled: (state, action: PayloadAction<boolean>) => {
      state.ultraModeEnabled = action.payload
      // Save to localStorage
      localStorage.setItem('ultraModeEnabled', String(action.payload))
    },
    loadSettings: (state) => {
      // Load from localStorage
      const bgMusic = localStorage.getItem('backgroundMusicEnabled')
      const clickSound = localStorage.getItem('clickSoundEnabled')
      const musicVolume = localStorage.getItem('musicVolume')
      const ultraMode = localStorage.getItem('ultraModeEnabled')

      if (bgMusic !== null) {
        state.backgroundMusicEnabled = bgMusic === 'true'
      }
      if (clickSound !== null) {
        state.clickSoundEnabled = clickSound === 'true'
      }
      if (musicVolume !== null) {
        const volume = parseFloat(musicVolume)
        if (!isNaN(volume) && volume >= 0 && volume <= 1) {
          state.musicVolume = volume
        }
      }
      if (ultraMode !== null) {
        state.ultraModeEnabled = ultraMode === 'true'
      }
    },
  },
})

export const { setBackgroundMusicEnabled, setClickSoundEnabled, setMusicVolume, setMusicDucked, setUltraModeEnabled, loadSettings } = settingsSlice.actions
export default settingsSlice.reducer

